<template>
  <b-modal
    id="SaveDeviceModal"
    size="lg"
    :no-close-on-backdrop="true"
    :no-close-on-esc="true"
    @hidden="doCancel"
  >
    <template
      slot="modal-header"
      slot-scope="{ close }"
    >
      <h5>{{ modalTitle }}</h5>
      <button
        class="btn btn--close"
        @click="close()"
      >
        <font-awesome-icon
          :icon="['fal', 'times']"
          class="icon alt"
        />
      </button>
    </template>

    <form-component>
      <form
        novalidate="novalidate"
        @submit.prevent
      >
        <b-card no-body>
          <b-tabs card>
            <b-tab
              title="System"
              active
            >
              <b-card-text>
                <input-text
                  id="nickname"
                  v-model="nickname"
                  name="nickname"
                  data-vv-name="nickname"
                  data-vv-as="Device Nickname"
                  label="Device Nickname"
                  type="text"
                  required
                  :disabled="!($store.getters.isAdminUser)"
                  :error="errors.first('nickname')"
                />

                <input-text
                  id="serial_number"
                  v-model="serial_number"
                  v-validate="'required'"
                  name="serial_number"
                  data-vv-name="serial_number"
                  data-vv-as="Device Serial"
                  label="Device Serial"
                  type="text"
                  required
                  :disabled="id > 0"
                  :error="errors.first('serial_number')"
                />

                <input-select
                  v-if="$store.getters.isKgbAdminUser"
                  id="organization_id"
                  v-model="organization_id"
                  v-validate="'required'"
                  name="organization_id"
                  data-vv-name="organization_id"
                  data-vv-as="Organization"
                  label="Organization"
                  :disabled="!$store.getters.isKgbAdminUser"
                  :required="true"
                  :error="errors.first('organization_id')"
                  :options="activeOrganizations"
                  :on-change="changedOrganization"
                  key-field="id"
                  display-field="name"
                />

                <input-select
                  v-if="$store.getters.isAdminUser && sites"
                  id="site_id"
                  v-model="site_id"
                  name="site_id"
                  data-vv-name="site_id"
                  data-vv-as="Site"
                  label="Site"
                  :disabled="!$store.getters.isOrgAdminAccess"
                  :options="sites"
                  key-field="id"
                  display-field="name"
                />

                <input-select
                  id="disabled"
                  v-model="disabled"
                  v-validate="'required'"
                  name="disabled"
                  data-vv-name="disabled"
                  data-vv-as="Status"
                  label="Status"
                  :disabled="!$store.getters.isKgbAdminUser"
                  :required="true"
                  :error="errors.first('disabled')"
                  :options="negitiveStatusOptions"
                />

                <input-select
                  id="dataMonitoring"
                  v-model="dataMonitoring"
                  v-validate="'required'"
                  name="dataMonitoring"
                  data-vv-name="dataMonitoring"
                  data-vv-as="Data Monitoring"
                  label="Data Monitoring"
                  :disabled="!$store.getters.isKgbAdminUser"
                  :required="true"
                  :error="errors.first('dataMonitoring')"
                  :options="statusOptions"
                />

                <input-select
                  id="engineeringUnits"
                  v-model="engineeringUnits"
                  v-validate="'required'"
                  name="engineeringUnits"
                  data-vv-name="engineeringUnits"
                  data-vv-as="Engineering Units"
                  label="Engineering Units"
                  :disabled="!$store.getters.isKgbAdminUser"
                  :required="true"
                  :error="errors.first('engineeringUnits')"
                  :options="statusOptions"
                />

                <input-select
                  id="allowDemo"
                  v-model="allowDemo"
                  v-validate="'required'"
                  name="allowDemo"
                  data-vv-name="allowDemo"
                  data-vv-as="Demo Mode"
                  label="Demo Mode"
                  :disabled="!$store.getters.isKgbAdminUser"
                  :required="true"
                  :error="errors.first('allowDemo')"
                  :options="statusOptions"
                />
                <input-select
                  id="allowDemoRemoteView"
                  v-model="allowDemoRemoteView"
                  v-validate="'required'"
                  name="allowDemoRemoteView"
                  data-vv-name="allowDemoRemoteView"
                  data-vv-as="Demo Mode Remote View"
                  label="Demo Mode Remote View"
                  :disabled="!$store.getters.isKgbAdminUser"
                  :required="true"
                  :error="errors.first('allowDemoRemoteView')"
                  :options="statusOptions"
                />
                <input-select
                  id="ownership"
                  v-model="ownership"
                  v-validate="'required'"
                  name="ownership"
                  data-vv-name="ownership"
                  data-vv-as="Ownership"
                  label="Ownership"
                  :disabled="!$store.getters.isKgbAdminUser"
                  :required="true"
                  :error="errors.first('ownership')"
                  :options="ownershipOptions"
                />
                <input-select
                  v-if="$store.getters.isKgbAdminUser"
                  id="downloadInfoOption"
                  v-model="downloadInfoOption"
                  v-validate="'required'"
                  name="downloadInfoOption"
                  data-vv-name="downloadInfoOption"
                  data-vv-as="Download Information Screen"
                  label="Download Information Screen"
                  :required="true"
                  :error="errors.first('downloadInfoOption')"
                  :options="downloadInfoOptions"
                />
              </b-card-text>
            </b-tab>
            <b-tab title="Data Management">
              <b-card-text>
                <input-select
                  id="offloadLocation"
                  v-model="offloadLocation"
                  v-validate="'required'"
                  name="offloadLocation"
                  data-vv-name="offloadLocation"
                  data-vv-as="Offload Location"
                  label="Offload Location"
                  :disabled="!$store.getters.isKgbAdminUser"
                  :required="true"
                  :error="errors.first('offloadLocation')"
                  :options="offloadLocationOptions"
                  @change="changeOffloadLocation"
                />
                <input-select
                  v-if="offloadLocation === 'web'"
                  id="archiveData"
                  v-model="archiveData"
                  v-validate="'required'"
                  name="archiveData"
                  data-vv-name="archiveData"
                  data-vv-as="Archive Data"
                  label="Archive Data"
                  :disabled="!$store.getters.currentUser.is_admin"
                  :required="true"
                  :error="errors.first('archiveData')"
                  :options="statusOptions"
                />
              </b-card-text>
            </b-tab>
            <b-tab title="Recorder Support">
              <b-card-text>
                <input-select
                  id="fa2100"
                  v-model="fa2100"
                  v-validate="'required'"
                  name="fa2100"
                  data-vv-name="fa2100"
                  data-vv-as="L3 - FA2100/FA2300"
                  label="L3 - FA2100/FA2300 (KGB750-100)"
                  :disabled="!$store.getters.isKgbAdminUser"
                  :required="true"
                  :error="errors.first('fa2100')"
                  :options="statusOptions"
                />
                <input-select
                  id="fa5000"
                  v-model="fa5000"
                  v-validate="'required'"
                  name="fa5000"
                  data-vv-name="fa5000"
                  data-vv-as="L3 - FA5000"
                  label="L3 - FA5000 (KGB750-106)"
                  :disabled="!$store.getters.isKgbAdminUser"
                  :required="true"
                  :error="errors.first('fa5000')"
                  :options="statusOptions"
                />
                <input-select
                  id="f1000"
                  v-model="f1000"
                  v-validate="'required'"
                  name="f1000"
                  data-vv-name="f1000"
                  data-vv-as="L3 - F1000"
                  label="L3 - F1000 (KGB750-101)"
                  :disabled="!$store.getters.isKgbAdminUser"
                  :required="true"
                  :error="errors.first('f1000')"
                  :options="statusOptions"
                />
                <input-select
                  id="srvivr"
                  v-model="srvivr"
                  v-validate="'required'"
                  name="srvivr"
                  data-vv-name="srvivr"
                  data-vv-as="L3 SRVIVR 1493100-1000/1493100-2000/1493100-300X"
                  label="L3 SRVIVR 1493100-1000/1493100-2000/1493100-300X (KGB750-105)"
                  :disabled="!$store.getters.isKgbAdminUser"
                  :required="true"
                  :error="errors.first('srvivr')"
                  :options="statusOptions"
                />
                <input-select
                  id="l3uqar"
                  v-model="l3uqar"
                  v-validate="'required'"
                  name="l3uqar"
                  data-vv-name="l3uqar"
                  data-vv-as="L3 uQAR-USB"
                  label="L3 uQAR-USB (KGB750-119)"
                  :disabled="!$store.getters.isKgbAdminUser"
                  :required="true"
                  :error="errors.first('l3uqar')"
                  :options="statusOptions"
                />
                <input-select
                  id="uacvfdr"
                  v-model="uacvfdr"
                  v-validate="'required'"
                  name="uacvfdr"
                  data-vv-name="uacvfdr"
                  data-vv-as="Universal - 1605/1606/1607"
                  label="Universal - 1605/1606/1607 (KGB750-104)"
                  :disabled="!$store.getters.isKgbAdminUser"
                  :required="true"
                  :error="errors.first('uacvfdr')"
                  :options="statusOptions"
                />
                <input-select
                  id="as4700"
                  v-model="as4700"
                  v-validate="'required'"
                  name="as4700"
                  data-vv-name="as4700"
                  data-vv-as="Honeywell - 980-4700/980-4710"
                  label="Honeywell - 980-4700/980-4710 (KGB750-102/KGB750-103)"
                  :disabled="!$store.getters.isKgbAdminUser"
                  :required="true"
                  :error="errors.first('as4700')"
                  :options="statusOptions"
                />
                <input-select
                  id="hw6020"
                  v-model="hw6020"
                  v-validate="'required'"
                  name="hw6020"
                  data-vv-name="hw6020"
                  data-vv-as="Honeywell - 980-6020/6022"
                  label="Honeywell - 980-6020/6022 (KGB750-109)"
                  :disabled="!$store.getters.isKgbAdminUser"
                  :required="true"
                  :error="errors.first('hw6020')"
                  :options="statusOptions"
                />
                <input-select
                  id="hw6023"
                  v-model="hw6023"
                  v-validate="'required'"
                  name="hw6023"
                  data-vv-name="hw6023"
                  data-vv-as="Honeywell - 980-6023"
                  label="Honeywell - 980-6023 (KGB750-115)"
                  :disabled="!$store.getters.isKgbAdminUser"
                  :required="true"
                  :error="errors.first('hw6023')"
                  :options="statusOptions"
                />
                <input-select
                  id="hw6025"
                  v-model="hw6025"
                  v-validate="'required'"
                  name="hw6025"
                  data-vv-name="hw6025"
                  data-vv-as="Honeywell - 980-6025"
                  label="Honeywell - 980-6025 (KGB750-122)"
                  :disabled="!$store.getters.isKgbAdminUser"
                  :required="true"
                  :error="errors.first('hw6025')"
                  :options="statusOptions"
                />
                <input-select
                  id="hwsrvivr"
                  v-model="hwsrvivr"
                  v-validate="'required'"
                  name="hwsrvivr"
                  data-vv-name="hwsrvivr"
                  data-vv-as="Honeywell - SRVIVR 980-6044/980-4131/980-60500"
                  label="Honeywell - SRVIVR 980-6044/980-4131/980-6050 (KGB750-105)"
                  :disabled="!$store.getters.isKgbAdminUser"
                  :required="true"
                  :error="errors.first('hwsrvivr')"
                  :options="statusOptions"
                />
                <input-select
                  id="hfr5"
                  v-model="hfr5"
                  v-validate="'required'"
                  name="hfr5"
                  data-vv-name="hfr5"
                  data-vv-as="Honeywell HFR5-C/D"
                  label="Honeywell HFR5-C/D (KGB750-108)"
                  :disabled="!$store.getters.isKgbAdminUser"
                  :required="true"
                  :error="errors.first('hfr5')"
                  :options="statusOptions"
                />
                <input-select
                  id="D51615"
                  v-model="D51615"
                  v-validate="'required'"
                  name="D51615"
                  data-vv-name="D51615"
                  data-vv-as="Penny+Giles - MPFR D51615"
                  label="Penny+Giles - MPFR D51615 (KGB750-110)"
                  :disabled="!$store.getters.isKgbAdminUser"
                  :required="true"
                  :error="errors.first('D51615')"
                  :options="statusOptions"
                />
                <input-select
                  id="fortressCamera"
                  v-model="fortressCamera"
                  v-validate="'required'"
                  name="fortressCamera"
                  data-vv-name="fortressCamera"
                  data-vv-as="Fortress Camera"
                  label="Fortress Camera (KGB750-114)"
                  :disabled="!$store.getters.isKgbAdminUser"
                  :required="true"
                  :error="errors.first('fortressCamera')"
                  :options="statusOptions"
                />
                <input-select
                  id="fortressRecorder"
                  v-model="fortressRecorder"
                  v-validate="'required'"
                  name="fortressRecorder"
                  data-vv-name="fortressRecorder"
                  data-vv-as="Fortress Recorder"
                  label="Fortress Recorder (KGB750-113)"
                  :disabled="!$store.getters.isKgbAdminUser"
                  :required="true"
                  :error="errors.first('fortressRecorder')"
                  :options="statusOptions"
                />
              </b-card-text>
            </b-tab>
            <b-tab title="ARINC">
              <b-card-text>
                <input-select
                  id="arinc717Transmit"
                  v-model="arinc717Transmit"
                  v-validate="'required'"
                  name="arinc717Transmit"
                  data-vv-name="arinc717Transmit"
                  data-vv-as="717 Transmit"
                  label="717 Transmit"
                  :disabled="!$store.getters.isKgbAdminUser"
                  :required="true"
                  :error="errors.first('arinc717Transmit')"
                  :options="statusOptions"
                />
                <input-select
                  id="arinc717Receive"
                  v-model="arinc717Receive"
                  v-validate="'required'"
                  name="arinc717Receive"
                  data-vv-name="arinc717Receive"
                  data-vv-as="717 Receive"
                  label="717 Receive"
                  :disabled="!$store.getters.isKgbAdminUser"
                  :required="true"
                  :error="errors.first('arinc717Receive')"
                  :options="statusOptions"
                />
                <input-select
                  id="arinc429Transmit"
                  v-model="arinc429Transmit"
                  v-validate="'required'"
                  name="arinc429Transmit"
                  data-vv-name="arinc429Transmit"
                  data-vv-as="429 Transmit"
                  label="429 Transmit"
                  :disabled="!$store.getters.isKgbAdminUser"
                  :required="true"
                  :error="errors.first('arinc429Transmit')"
                  :options="statusOptions"
                />
                <input-select
                  id="arinc429Receive"
                  v-model="arinc429Receive"
                  v-validate="'required'"
                  name="arinc429Receive"
                  data-vv-name="arinc429Receive"
                  data-vv-as="429 Receive"
                  label="429 Receive"
                  :disabled="!$store.getters.isKgbAdminUser"
                  :required="true"
                  :error="errors.first('arinc429Receive')"
                  :options="statusOptions"
                />
              </b-card-text>
            </b-tab>
            <b-tab title="MIL-STD">
              <b-card-text>
                <input-select
                  id="milStd1553Transmit"
                  v-model="milStd1553Transmit"
                  v-validate="'required'"
                  name="milStd1553Transmit"
                  data-vv-name="milStd1553Transmit"
                  data-vv-as="1553 Transmit"
                  label="1553 Transmit"
                  :disabled="!$store.getters.isKgbAdminUser"
                  :required="true"
                  :error="errors.first('milStd1553Transmit')"
                  :options="statusOptions"
                />
                <input-select
                  id="milStd1553Receive"
                  v-model="milStd1553Receive"
                  v-validate="'required'"
                  name="milStd1553Receive"
                  data-vv-name="milStd1553Receive"
                  data-vv-as="1553 Receive"
                  label="1553 Receive"
                  :disabled="!$store.getters.isKgbAdminUser"
                  :required="true"
                  :error="errors.first('milStd1553Receive')"
                  :options="statusOptions"
                />
              </b-card-text>
            </b-tab>
            <b-tab title="Connectivity">
              <b-card-text>
                <input-select
                  id="allowWifi"
                  v-model="allowWifi"
                  v-validate="'required'"
                  name="allowWifi"
                  data-vv-name="allowWifi"
                  data-vv-as="WiFi"
                  label="WiFi"
                  :disabled="!$store.getters.isKgbAdminUser"
                  :required="true"
                  :error="errors.first('allowWifi')"
                  :options="statusOptions"
                />
                <input-select
                  id="allowEthernet"
                  v-model="allowEthernet"
                  v-validate="'required'"
                  name="allowEthernet"
                  data-vv-name="allowEthernet"
                  data-vv-as="Ethernet"
                  label="Ethernet"
                  :disabled="!$store.getters.isKgbAdminUser"
                  :required="true"
                  :error="errors.first('allowEthernet')"
                  :options="statusOptions"
                />
                <input-select
                  id="allowBluetooth"
                  v-model="allowBluetooth"
                  v-validate="'required'"
                  name="allowBluetooth"
                  data-vv-name="allowBluetooth"
                  data-vv-as="Bluetooth"
                  label="Bluetooth"
                  :disabled="!$store.getters.isKgbAdminUser"
                  :required="true"
                  :error="errors.first('allowBluetooth')"
                  :options="statusOptions"
                />
                <input-select
                  id="allowCellular"
                  v-model="allowCellular"
                  v-validate="'required'"
                  name="allowCellular"
                  data-vv-name="allowCellular"
                  data-vv-as="Cellular"
                  label="Cellular"
                  :disabled="!$store.getters.isKgbAdminUser"
                  :required="true"
                  :error="errors.first('allowCellular')"
                  :options="statusOptions"
                />
              </b-card-text>
            </b-tab>
            <b-tab
              v-if="allowedEncryption"
              title="Encryption"
            >
              <input-select
                id="isAllowedDataEncryption"
                v-model="isAllowedDataEncryption"
                v-validate="'required'"
                name="isAllowedDataEncryption"
                data-vv-name="isAllowedDataEncryption"
                data-vv-as="Encryption"
                label="Encryption"
                :disabled="!($store.getters.isAdminUser)"
                :required="true"
                :error="errors.first('isAllowedDataEncryption')"
                :options="statusOptions"
              />

              <div v-if="isAllowedDataEncryption === 'true'">
                <h6>Current Public Keys</h6>
                <responsive-table>
                  <thead class="inline-header">
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Level</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="encryptionKey of encryptionKeys"
                      :key="encryptionKey.id"
                    >
                      <td>{{ encryptionKey.name }} </td>
                      <td>{{ encryptionKey.email }}</td>
                      <td>{{ encryptionKey.device ? 'Device' : 'Organization' }}</td>
                      <td class="actions">
                        <button
                          v-if="$store.getters.isAdminUser && encryptionKey.device"
                          v-b-tooltip.hover
                          class="red-hover"
                          title="Delete"
                          type="button"
                          @click="deleteEncryptionKey(organization_id, id, encryptionKey.id)"
                        >
                          <font-awesome-icon
                            :icon="['fas', 'trash']"
                            class="icon alt"
                          />
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </responsive-table>

                <button
                  v-if="id > 0 && $store.getters.isAdminUser"
                  title="Add"
                  type="button"
                  class="btn btn--primary"
                  @click="addPublicKey(organization_id, id, serial_number)"
                >
                  Add New Device PGP Key
                </button>
              </div>
            </b-tab>
          </b-tabs>
        </b-card>
      </form>
    </form-component>

    <template
      slot="modal-footer"
    >
      <button
        class="btn"
        type="button"
        @click="doCancel()"
      >
        Cancel
      </button>
      <button
        v-if="$store.getters.isAdminUser"
        class="btn btn--primary"
        type="button"
        :disabled="saving"
        @click="saveDevice()"
      >
        <font-awesome-icon
          v-if="saving"
          :spin="true"
          :icon="['fas', 'spinner']"
          class="icon alt"
        />
        <span v-else>Save</span>
      </button>
    </template>
  </b-modal>
</template>

<script>
/* eslint-disable vue/require-default-prop */
import FormComponent from '@/components/FormComponent.vue';
import ResponsiveTable from '@/components/ResponsiveTable.vue';
import InputSelect from '@/components/inputs/InputSelect.vue';
import InputText from '@/components/inputs/InputText.vue';

const defaultSites = [{ id: '-1', name: 'Primary' }];
export default {
  name: 'SaveDeviceModal',
  components: {
    FormComponent,
    InputText,
    InputSelect,
    ResponsiveTable,
  },
  props: {
    doCancel: {
      type: Function,
    },
    organizations: {
      type: Array,
    },
    doSaveDevice: {
      type: Function,
    },
    deleteEncryptionKey: {
      type: Function,
    },
    addPublicKey: {
      type: Function,
    },
    getOrganizationSites: {
      type: Function,
    },
  },
  data() {
    return {
      id: 0,
      serial_number: '',
      nickname: '',
      utcOffset: '-05',
      organization_id: '',
      disabled: '',
      dataMonitoring: '',
      engineeringUnits: '',
      webOffload: '',
      usbOffload: '',
      offloadLocation: '',
      archiveData: '',
      fa2100: '',
      fa5000: '',
      f1000: '',
      srvivr: '',
      uacvfdr: '',
      as4700: '',
      hw6020: '',
      hw6023: '',
      hw6025: '',
      hwsrvivr: '',
      D51615: '',
      hfr5: '',
      fortressCamera: '',
      fortressRecorder: '',
      l3uqar: '',
      arinc717Transmit: '',
      arinc717Receive: '',
      arinc429Transmit: '',
      arinc429Receive: '',
      milStd1553Transmit: '',
      milStd1553Receive: '',
      allowWifi: '',
      allowEthernet: '',
      allowBluetooth: '',
      allowCellular: '',
      allowDemo: '',
      allowDemoRemoteView: '',
      isAllowedDataEncryption: '',
      ownership: '',
      downloadInfoOption: '',
      encryptionKeys: [],
      sites: [],
      site_id: '-1',
      utcOptions: [
        { id: '1', display_label: '+01' },
        { id: '2', display_label: '+02' },
        { id: '3', display_label: '+03' },
        { id: '3.5', display_label: '+03:30' },
        { id: '4', display_label: '+04' },
        { id: '4.5', display_label: '+04:30' },
        { id: '5', display_label: '+05' },
        { id: '5.5', display_label: '+05:30' },
        { id: '5.45', display_label: '+05:45' },
        { id: '6', display_label: '+06' },
        { id: '6.5', display_label: '+06:30' },
        { id: '7', display_label: '+07' },
        { id: '8', display_label: '+08' },
        { id: '8.75', display_label: '+08:45' },
        { id: '9', display_label: '+09' },
        { id: '9.5', display_label: '+09:30' },
        { id: '10', display_label: '+10' },
        { id: '10.5', display_label: '+10:30' },
        { id: '11', display_label: '+11' },
        { id: '12', display_label: '+12' },
        { id: '12.75', display_label: '+12:45' },
        { id: '13', display_label: '+13' },
        { id: '13.75', display_label: '+13:45' },
        { id: '14', display_label: '+14' },
        { id: '-1', display_label: '−01' },
        { id: '-2', display_label: '−02' },
        { id: '-2.5', display_label: '−02:30' },
        { id: '-3', display_label: '−03' },
        { id: '-3.5', display_label: '−03:30' },
        { id: '-4', display_label: '−04' },
        { id: '-5', display_label: '−05' },
        { id: '-6', display_label: '−06' },
        { id: '-7', display_label: '−07' },
        { id: '-8', display_label: '−08' },
        { id: '-9', display_label: '−09' },
        { id: '-9.5', display_label: '−09:30' },
        { id: '-10', display_label: '−10' },
        { id: '-11', display_label: '−11' },
        { id: '-12', display_label: '−12' },
      ],
      statusOptions: [
        { id: 'true', display_label: 'Enabled' },
        { id: 'false', display_label: 'Disabled' },
      ],
      negitiveStatusOptions: [
        { id: 'false', display_label: 'Enabled' },
        { id: 'true', display_label: 'Disabled' },
      ],
      offloadOptions: [
        { id: 'none', display_label: 'None' },
        { id: 'all', display_label: 'All' },
        { id: 'data', display_label: 'DataOnly' },
        { id: 'log', display_label: 'LogOnly' },
      ],
      ownershipOptions: [
        { id: 'owned', display_label: 'Owned' },
        { id: 'leased', display_label: 'Leased' },
        { id: 'rented', display_label: 'Rented' },
      ],
      downloadInfoOptions: [
        { id: 'KGB', display_label: 'KGB' },
        { id: 'SkyWest', display_label: 'SkyWest' },
      ],
      offloadLocationOptions: [
        { id: 'web', display_label: 'Web' },
        { id: 'usb', display_label: 'USB' },
      ],
      saving: false,
    };
  },
  computed: {
    modalTitle() {
      if (this.id === 0) {
        return 'New Device';
      }
      if (!this.$store.getters.isKgbAdminUser) {
        return `Device ${this.serial_number}`;
      }
      return `Edit Device ${this.serial_number}`;
    },
    activeOrganizations() {
      return this.organizations.filter(x => !x.is_removed);
    },
    allowedEncryption() {
      const currentOrg = this.organizations.find(x => x.id === Number(this.organization_id));
      return currentOrg && currentOrg.is_allowed_data_encryption && this.offloadLocation === 'web';
    },
  },
  mounted() {

  },
  methods: {
    saveDevice() {
      this.$validator.validateAll().then((result) => {
        if (result) {
          this.errors.clear();
          this.saving = true;
          this.doSaveDevice(this.id, {
            serial_number: this.serial_number,
            nickname: this.nickname.trim() === '' ? null : this.nickname.trim(),
            utcOffset: -5,
            organization: this.organization_id,
            site: (this.site_id === '-1') ? null : Number(this.site_id),
            disabled: this.disabled,
            dataMonitoring: this.dataMonitoring,
            engineeringUnits: this.engineeringUnits,
            ownership: this.ownership,
            downloadInfoOption: this.downloadInfoOption,
            webOffload: this.webOffload,
            usbOffload: this.usbOffload,
            offloadLocation: this.offloadLocation,
            archiveData: this.archiveData,
            fa2100: this.fa2100,
            fa5000: this.fa5000,
            hw6020: this.hw6020,
            hw6023: this.hw6023,
            hw6025: this.hw6025,
            f1000: this.f1000,
            srvivr: this.srvivr,
            uacvfdr: this.uacvfdr,
            as4700: this.as4700,
            hwsrvivr: this.hwsrvivr,
            D51615: this.D51615,
            hfr5: this.hfr5,
            fortressCamera: this.fortressCamera,
            fortressRecorder: this.fortressRecorder,
            l3uqar: this.l3uqar,
            arinc717Transmit: this.arinc717Transmit,
            arinc717Receive: this.arinc717Receive,
            arinc429Transmit: this.arinc429Transmit,
            arinc429Receive: this.arinc429Receive,
            milStd1553Transmit: this.milStd1553Transmit,
            milStd1553Receive: this.milStd1553Receive,
            allowWifi: this.allowWifi,
            allowEthernet: this.allowEthernet,
            allowBluetooth: this.allowBluetooth,
            allowCellular: this.allowCellular,
            allowDemo: this.allowDemo,
            allowDemoRemoteView: this.allowDemoRemoteView,
            isAllowedDataEncryption: this.isAllowedDataEncryption,
          }, () => {
            this.saving = false;
          });
        }
      });
    },
    populateSites() {
      this.sites = null;
      this.getOrganizationSites(this.organization_id)
        .then((sites) => { this.sites = [...defaultSites, ...sites]; });
    },
    setDeviceModal(model) {
      this.saving = false;
      this.id = model.id || 0;
      this.encryptionKeys = [];
      this.serial_number = model.serial_number || '';
      this.nickname = model.nickname || '';
      this.utcOffset = model.utcOffset || '-5';
      this.organization_id = `${model.organization_id || ''}`;
      if (model.organization_id) {
        this.populateSites();
      }
      this.site_id = `${model.site_id || '-1'}`;
      this.disabled = 'false';
      if (this.id && model.disabled) {
        this.disabled = 'true';
      }
      this.dataMonitoring = this.id && model.dataMonitoring ? 'true' : 'false';
      this.engineeringUnits = this.id && model.engineeringUnits ? 'true' : 'false';
      this.webOffload = (this.id === 0) ? 'all' : model.webOffload || 'none';
      this.usbOffload = model.usbOffload || 'none';
      this.offloadLocation = model.offloadLocation || 'web';
      this.archiveData = (this.id === 0) || model.archiveData ? 'true' : 'false';
      this.ownership = model.ownership || 'owned';
      this.downloadInfoOption = model.downloadInfoOption || 'KGB';
      this.fa2100 = (this.id === 0) || model.fa2100 ? 'true' : 'false';
      this.fa5000 = (this.id === 0) || model.fa5000 ? 'true' : 'false';
      this.f1000 = (this.id === 0) || model.f1000 ? 'true' : 'false';
      this.srvivr = (this.id === 0) || model.srvivr ? 'true' : 'false';
      this.uacvfdr = (this.id === 0) || model.uacvfdr ? 'true' : 'false';
      this.as4700 = (this.id === 0) || model.as4700 ? 'true' : 'false';
      this.hw6020 = (this.id === 0) || model.hw6020 ? 'true' : 'false';
      this.hw6023 = (this.id === 0) || model.hw6023 ? 'true' : 'false';
      this.hw6025 = (this.id === 0) || model.hw6025 ? 'true' : 'false';
      this.hwsrvivr = (this.id === 0) || model.hwsrvivr ? 'true' : 'false';
      this.D51615 = (this.id === 0) || model.D51615 ? 'true' : 'false';
      this.hfr5 = (this.id === 0) || model.hfr5 ? 'true' : 'false';
      this.fortressCamera = (this.id === 0) || model.fortressCamera ? 'true' : 'false';
      this.fortressRecorder = (this.id === 0) || model.fortressRecorder ? 'true' : 'false';
      this.l3uqar = (this.id === 0) || model.l3uqar ? 'true' : 'false';

      this.arinc717Transmit = this.id && model.arinc717Transmit ? 'true' : 'false';
      this.arinc717Receive = this.id && model.arinc717Receive ? 'true' : 'false';
      this.arinc429Transmit = this.id && model.arinc429Transmit ? 'true' : 'false';
      this.arinc429Receive = this.id && model.arinc429Receive ? 'true' : 'false';
      this.milStd1553Transmit = this.id && model.milStd1553Transmit ? 'true' : 'false';
      this.milStd1553Receive = this.id && model.milStd1553Receive ? 'true' : 'false';
      this.allowWifi = (this.id === 0) || model.allowWifi ? 'true' : 'false';
      this.allowEthernet = (this.id === 0) || model.allowEthernet ? 'true' : 'false';
      this.allowBluetooth = (this.id === 0) || model.allowBluetooth ? 'true' : 'false';
      this.allowCellular = this.id && model.allowCellular ? 'true' : 'false';
      this.allowDemo = this.id && model.allowDemo ? 'true' : 'false';
      this.allowDemoRemoteView = this.id && model.allowDemoRemoteView ? 'true' : 'false';
      this.isAllowedDataEncryption = this.id && model.isAllowedDataEncryption ? 'true' : 'false';
    },
    changedOrganization() {
      this.encryptionKeys = [];
      this.site_id = '-1';
      this.populateSites();
    },
    changeOffloadLocation() {
      if (this.offloadLocation !== 'web') {
        this.isAllowedDataEncryption = 'false';
      }
    },
    setEncryptionKeys(keys) {
      this.encryptionKeys = keys;
    },
  },
};
</script>

<style scoped lang="scss">

</style>
